import { useState } from "react";
import useRead from "./useRead";
import usePut from "./usePut";
import toast from "react-hot-toast";

export const useEmployeeTdsData = () => {
  const [currentEmployeeParams, setCurrentEmployeeParams] = useState(null);

  // Fetch employee TDS data
  const { list: employeeTdsData, setGetListParams: setEmployeeTdsParams } = useRead({
    url: "tds/get-user-response",
    initialData: {},
    method: "GET",
    enabled: !!currentEmployeeParams,
  });

  // Approve single field
  const approveSingleField = usePut({
    url: "tds/approve-single-field",
    refreshUrl: "tds/get-user-response",
    onSuccess: (response) => {
      toast.success("Field approved successfully");
      // Refresh the current employee data
      if (currentEmployeeParams) {
        setEmployeeTdsParams(currentEmployeeParams);
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to approve field");
    },
  });

  // Reject single field
  const rejectSingleField = usePut({
    url: "tds/reject-single-field",
    refreshUrl: "tds/get-user-response",
    onSuccess: (response) => {
      toast.success("Field rejected successfully");
      // Refresh the current employee data
      if (currentEmployeeParams) {
        setEmployeeTdsParams(currentEmployeeParams);
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to reject field");
    },
  });

  // Approve main response (Consider for IT)
  const approveMainResponse = usePut({
    url: "tds/approve-main-response",
    refreshUrl: "tds/get-user-response",
    onSuccess: (response) => {
      toast.success("Employee submission approved and considered for IT successfully");
      // Refresh the current employee data
      if (currentEmployeeParams) {
        setEmployeeTdsParams(currentEmployeeParams);
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to approve main response");
    },
  });

  // Load employee TDS data
  const loadEmployeeTdsData = (employeeSubmissionId, companyEmployeeId) => {
    const params = {
      employee_tds_submission_id: employeeSubmissionId,
      company_employee_id: companyEmployeeId,
      screen: "MASTER",
    };
    setCurrentEmployeeParams(params);
    setEmployeeTdsParams(params);
  };

  // Handle field approval
  const handleApproval = (approvalData) => {
    approveSingleField.handleAuth.mutate(approvalData);
  };

  // Handle field rejection
  const handleRejection = (rejectionData) => {
    rejectSingleField.handleAuth.mutate(rejectionData);
  };

  // Handle main response approval (Consider for IT)
  const handleMainApproval = (employeeTdsSubmissionId) => {
    approveMainResponse.handleAuth.mutate({
      employee_tds_submission_id: employeeTdsSubmissionId,
    });
  };

  // Check if all items are approved/rejected (none are awaiting approval)
  const isAllItemsProcessed = (employeeTdsData) => {
    if (!employeeTdsData) return false;

    const itemsToCheck = [];

    // House rent items
    if (employeeTdsData.house_rent) {
      itemsToCheck.push(...employeeTdsData.house_rent.map(item => item.status));
    }

    // Home loan (self occupied property) - check individual field statuses
    if (employeeTdsData.home_loan) {
      // Only check fields that have amounts > 0
      if (parseFloat(employeeTdsData.home_loan.principal_amount || 0) > 0) {
        itemsToCheck.push(employeeTdsData.home_loan.p_amt_approved_status || "APPROVAL_AWAITING");
      }
      if (parseFloat(employeeTdsData.home_loan.interest_amount || 0) > 0) {
        itemsToCheck.push(employeeTdsData.home_loan.int_amt_approved_status || "APPROVAL_AWAITING");
      }
    }

    // Other source income items
    if (employeeTdsData.other_source_income) {
      const otherIncome = employeeTdsData.other_source_income;
      if (otherIncome.income_from_other_sources && parseFloat(otherIncome.income_from_other_sources) > 0) {
        itemsToCheck.push(otherIncome.income_from_other_sources_status || "APPROVAL_AWAITING");
      }
      if (otherIncome.interest_from_savings && parseFloat(otherIncome.interest_from_savings) > 0) {
        itemsToCheck.push(otherIncome.interest_from_savings_status || "APPROVAL_AWAITING");
      }
      if (otherIncome.interest_from_fixed_deposits && parseFloat(otherIncome.interest_from_fixed_deposits) > 0) {
        itemsToCheck.push(otherIncome.interest_from_fixed_deposits_status || "APPROVAL_AWAITING");
      }
      if (otherIncome.interest_from_national_savings && parseFloat(otherIncome.interest_from_national_savings) > 0) {
        itemsToCheck.push(otherIncome.interest_from_national_savings_status || "APPROVAL_AWAITING");
      }
    }

    // Rental income items - check individual field statuses for each property
    if (employeeTdsData.rental_income) {
      employeeTdsData.rental_income.forEach(property => {
        // Always check annual rent and municipal taxes
        if (parseFloat(property.annual_rent_received || 0) > 0) {
          itemsToCheck.push(property.annual_rent_rec_status || "APPROVAL_AWAITING");
        }
        if (parseFloat(property.municipal_taxes_paid || 0) > 0) {
          itemsToCheck.push(property.municipal_tax_status || "APPROVAL_AWAITING");
        }
        // Check home loan related fields if applicable
        if (property.repaying_home_loan === "1") {
          if (parseFloat(property.interest_amount || 0) > 0) {
            itemsToCheck.push(property.int_amt_approved_status || "APPROVAL_AWAITING");
          }
          // Principal amount only for old tax regime
          if (employeeTdsData.main_submission_data?.employee_regime_selection?.regime === 'old' && 
              parseFloat(property.principal_amount || 0) > 0) {
            itemsToCheck.push(property.p_amt_approved_status || "APPROVAL_AWAITING");
          }
        }
      });
    }

    // 80C investments
    if (employeeTdsData.investments_80C) {
      itemsToCheck.push(...employeeTdsData.investments_80C.map(item => item.status));
    }

    // 80D exemptions
    if (employeeTdsData.exemptions_80D) {
      itemsToCheck.push(...employeeTdsData.exemptions_80D.map(item => item.status));
    }

    // Other investments
    if (employeeTdsData.other_investments) {
      itemsToCheck.push(...employeeTdsData.other_investments.map(item => item.status));
    }

    // Return true if no items are awaiting approval
    return itemsToCheck.length > 0 && !itemsToCheck.includes("APPROVAL_AWAITING");
  };

  return {
    employeeTdsData: employeeTdsData?.data?.data,
    isLoadingTdsData: employeeTdsData?.isLoading,
    loadEmployeeTdsData,
    handleApproval,
    handleRejection,
    handleMainApproval,
    isApprovingField: approveSingleField.isLoading,
    isRejectingField: rejectSingleField.isLoading,
    isApprovingMainResponse: approveMainResponse.isLoading,
    isAllItemsProcessed,
  };
};
